import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import compareDesc from 'date-fns/compareDesc';
import isFunction from 'lodash/isFunction';
import parseISO from 'date-fns/parseISO';

import { formatISOAsRelative } from '../../../utils/date_utils';
import { preserveWorkspaceURL } from '../../../utils/url_utils';

import DataTable from '../../common/DataTable/DataTable';
import LinkLabel from '../../common/Labels/LinkLabel';

import OrchestrationStatusBadge from '../OrchestrationStatusBadge/OrchestrationStatusBadge';

const transformOrchestrationData = (orchestrationRows = []) => orchestrationRows?.map(orchestrationItem => ({
  id: orchestrationItem?.id,
  name: orchestrationItem?.name,
  workspaceId: orchestrationItem?.workspace_id,
  sha: orchestrationItem.sha,
  fileName: orchestrationItem.file_name,
  taskId: orchestrationItem.task_id,
  ownerId: orchestrationItem.owner_id,
  status: orchestrationItem.status,
  createdAt: orchestrationItem.created_at,
  updatedAt: orchestrationItem.updated_at,
}))

const sortOrchestrationData = (orchestrationRows = []) => orchestrationRows.sort((itemA, itemB) => {
  const firstDate = parseISO(itemA.createdAt);
  const secondDate = parseISO(itemB.createdAt);

  return compareDesc(firstDate, secondDate);
})

const RelativeDateCellRenderer = ({ value }) => formatISOAsRelative(value);
const StatusCellRenderer = ({ value }) => <OrchestrationStatusBadge status={value} textColor="text-white" />
const WorkloadCellRenderer = ({ row, value }) => {
  const workloadId = row?.original?.id
  const nullGuardedValue = value || '---';

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-row flex-wrap items-center mb-2">
        <LinkLabel
          to={preserveWorkspaceURL(`/orchestrations/${workloadId}`)}
          size="2xl"
          title={nullGuardedValue}
          italic={!value}
          bold={!!value}
        >
          {nullGuardedValue}
        </LinkLabel>
      </div>
    </div>
  )
}

const WorkloadIDRenderer = ({ value }) => (
  <div className="flex flex-col p-2">
    <div className="flex flex-row flex-wrap items-center mb-2">
      <LinkLabel
        to={preserveWorkspaceURL(`/orchestrations/${value}`)}
        size="md"
        title={value}
        bold
      >
        {value}
      </LinkLabel>
    </div>
  </div>
)

export const OrchestrationDataTable = ({ orchestrationData = [] }) => {
  const history = useHistory();

  const columns = useMemo(() => [
    {
      Header: 'Workload',
      accessor: 'name',
      Cell: WorkloadCellRenderer,
      width: 160
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: StatusCellRenderer,
      width: 80
    },
    {
      Header: 'Created At',
      accessor: 'createdAt',
      Cell: RelativeDateCellRenderer,
      width: 80,
    },
    {
      Header: 'ID',
      accessor: 'id',
      Cell: WorkloadIDRenderer,
      width: 120,
    },
  ], []);
  const data = useMemo(() => sortOrchestrationData(transformOrchestrationData(orchestrationData)), [orchestrationData]);
  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )

  const pushHistory = history?.push;
  const onItemClick = useCallback((row) => {
    const workloadId = row?.original?.id
    if (workloadId && isFunction(pushHistory)) {
      pushHistory(preserveWorkspaceURL(`/orchestrations/${workloadId}`)(history.location))
    }
  }, [history.location, pushHistory]);

  return (
    <DataTable
      id="orchestration-data-table"
      columns={columns}
      data={data}
      defaultColumn={defaultColumn}
      onItemClick={onItemClick}
    />
  )
};

export default OrchestrationDataTable;
